import DateDialect from "../Enums/DateDialect";
import DateRestriction from "../Enums/DateRestriction";
import LngLatFormat from "../Enums/LngLatFormat";
import BaseModel from "./BaseModel";
import CommentVisibility from "./Enums/CommentVisibility";
import VesselIDType from "./Enums/VesselIDType";

class Company extends BaseModel {
	constructor(data) {
		super({}, Company.defaults);
		this.data = data;
	}

	isNew() {
		return typeof this.id === "undefined" || this.id === -1;
	}

	get id() {
		return this.get("id");
	}

	get name() {
		return this.get("name");
	}

	get imos() {
		return this.get("imos");
	}

	get tierId() {
		return this.get("tier_id");
	}

	get featureOverrides() {
		return this.get("company_features");
	}

	get tierName() {
		if (this.tierId <= 0) {
			return "None";
		}
		return this.get("tier_name");
	}

	get tierUserLimit() {
		return this.get("tier_user_limit");
	}

	get extraData() {
		const extra = this.get("extra_data");
		extra.map = extra.map || {};
		extra.map.home = extra.map.home || {};
		extra.map.home.lng = extra.map.home.lng || Company.defaults.extra_data.map.home.lng;
		extra.map.home.lat = extra.map.home.lat || Company.defaults.extra_data.map.home.lat;
		extra.map.home.zoom = extra.map.home.zoom || Company.defaults.extra_data.map.home.zoom;

		extra.settings = extra.settings || {};
		extra.settings.date_dialect = extra.settings.date_dialect || Company.defaults.extra_data.settings.date_dialect;
		extra.settings.lnglat_format = extra.settings.lnglat_format || Company.defaults.extra_data.settings.lnglat_format;

		extra.vessel_history = extra.vessel_history || {};
		extra.vessel_history.restricted_imos = extra.vessel_history.restricted_imos || Company.defaults.extra_data.vessel_history.restricted_imos;
		extra.vessel_history.restricted_mmsis = extra.vessel_history.restricted_mmsis || Company.defaults.extra_data.vessel_history.restricted_mmsis;
		extra.vessel_history.date_restriction = extra.vessel_history.date_restriction || Company.defaults.extra_data.vessel_history.date_restriction;
		extra.vessel_history.date_restriction_value = extra.vessel_history.date_restriction_value || Company.defaults.extra_data.vessel_history.date_restriction_value;
		extra.vessel_history.oppd_sample_length = extra.vessel_history.oppd_sample_length || Company.defaults.extra_data.vessel_history.oppd_sample_length;

		extra.comments = extra.comments || {};
		extra.comments.default_thread_visibility = extra.comments.default_thread_visibility || Company.defaults.extra_data.comments.default_thread_visibility;

		return extra;
	}

	get dateDialect() {
		return this.extraData.settings.date_dialect;
	}

	get lngLatFormat() {
		return this.extraData.settings.lnglat_format;
	}

	get vesselHistoryDateRestriction() {
		return this.extraData.vessel_history.date_restriction;
	}

	get vesselHistoryDateRestrictionValue() {
		return this.extraData.vessel_history.date_restriction_value;
	}

	get mapHomeLng() {
		return this.extraData.map.home.lng;
	}

	get mapHomeLat() {
		return this.extraData.map.home.lat;
	}

	get mapHomeZoom() {
		return this.extraData.map.home.zoom;
	}

	get defaultThreadVisibility() {
		return this.extraData.comments.default_thread_visibility;
	}

	getVesselHistoryRestrictedInputs(type) {
		const parse = (str) => {
			if (str.indexOf(",") >= 0) {
				return str.split(",");
			}
			if (str.length > 0) {
				return [str];
			}
			return [];
		};
		if (type === VesselIDType.IMO) {
			return parse(this.vesselHistoryRestrictedIMOs);
		}
		else if (type === VesselIDType.MMSI) {
			return parse(this.vesselHistoryRestrictedMMSIs);
		}
		return [];
	}

	get vesselHistoryRestrictedIMOs() {
		return this.extraData.vessel_history.restricted_imos;
	}

	get vesselHistoryRestrictedMMSIs() {
		return this.extraData.vessel_history.restricted_mmsis;
	}

	hasAlertTypeROIPair(alertTypeCode, roiId) {
		const pairs = this.get("alert_type_roi_pairs");
		for (let i = 0; i < pairs.length; i++) {
			if (pairs[i].alert_type_code === alertTypeCode && pairs[i].roi_id === roiId) {
				return true;
			}
		}
		return false;
	}

	get userCount() {
		return this.get("user_count");
	}

	update(data) {
		super.setData(data);
	}

	asSaveData() {
		return {
			id: this.data.id,
			name: this.data.name,
			extra_data: this.data.extra_data
		};
	}
}
Company.defaults = { // Defaults
	id: -1,
	name: "",
	imos: "",
	tier_id: -1,
	tier_name: "",
	tier_user_count: -1,
	user_count: 0,
	company_features: {},
	extra_data: {
		map: {
			home: {
				lng: 25,
				lat: 25,
				zoom: 2
			}
		},
		vessel_history: {
			restricted_imos: "",
			restricted_mmsis: "",
			date_restriction: DateRestriction.NONE,
			date_restriction_value: "",
			oppd_sample_length: 86400
			// request_data_segmentation: new StrideSegmentation()
		},
		comments: {
			default_thread_visibility: CommentVisibility.PROTECTED
		},
		settings: {
			date_dialect: DateDialect.DMY,
			lnglat_format: LngLatFormat.DECIMAL
		}
	},
	alert_type_roi_pairs: []
};

export default Company;
